import { Check, Lock, Star } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { APPEAL_TYPES, appealTypesLabel } from "@/lib/document-kinds";
import type { AppealType } from "@/api/types";

interface AppealTypePickerProps {
  /** Ordered selection; index 0 is the primary (anchors the claim shape). */
  selected: AppealType[];
  onToggle: (s: AppealType) => void;
  onMakePrimary: (s: AppealType) => void;
  lockedAppealTypes?: AppealType[] | null;
}

export function AppealTypePicker({
  selected,
  onToggle,
  onMakePrimary,
  lockedAppealTypes = null,
}: AppealTypePickerProps) {
  const isLocked = !!lockedAppealTypes && lockedAppealTypes.length > 0;

  return (
    <div className="space-y-3">
      {isLocked ? (
        <div className="flex items-center gap-2 rounded-md border border-violet-200 bg-violet-50/60 px-3 py-2 text-[12px] text-violet-800">
          <Lock className="w-3.5 h-3.5" />
          Locked to{" "}
          <span className="font-medium">{appealTypesLabel(lockedAppealTypes)}</span>
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground">
          Pick one or more denial reasons. The{" "}
          <span className="font-medium text-violet-700">primary</span> (first
          selected) sets the claim type; the rest are layered across the claim's
          lines as a mixed denial.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 max-h-[420px] overflow-y-auto">
        {APPEAL_TYPES.map((s) => {
          const idx = selected.indexOf(s.slug);
          const isPicked = idx >= 0;
          const isPrimary = idx === 0;
          const disabled = isLocked && !isPicked;
          return (
            <div
              key={s.slug}
              role="checkbox"
              aria-checked={isPicked}
              aria-disabled={isLocked}
              tabIndex={isLocked ? -1 : 0}
              onClick={() => !isLocked && onToggle(s.slug)}
              onKeyDown={(e) => {
                if (isLocked) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle(s.slug);
                }
              }}
              className={
                "relative text-left rounded-lg border-2 p-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30 " +
                (isLocked ? "cursor-not-allowed " : "cursor-pointer ") +
                (isPicked
                  ? "border-foreground bg-slate-50 ring-4 ring-foreground/5"
                  : "border-border bg-background hover:border-slate-300") +
                (disabled ? " opacity-40" : "")
              }
            >
              <div className="flex items-center gap-2 mb-1.5 flex-wrap pr-6">
                <span className="font-semibold">{s.title}</span>
                <Badge variant="violet" className="font-mono text-[10px]">
                  CARC {s.carc}
                </Badge>
                {isPrimary && (
                  <Badge className="text-[10px] gap-1 bg-violet-600">
                    <Star className="w-2.5 h-2.5" />
                    Primary
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground leading-5">
                {s.description}
              </p>

              {/* checkbox indicator (top-right) — toggles via the card click */}
              <span
                className={
                  "absolute top-3 right-3 flex h-4 w-4 items-center justify-center rounded-[4px] border " +
                  (isPicked
                    ? "border-foreground bg-foreground text-background"
                    : "border-slate-300 bg-background")
                }
              >
                {isPicked && <Check className="h-3 w-3" />}
              </span>

              {/* "Make primary" affordance on selected, non-primary cards */}
              {!isLocked && isPicked && !isPrimary && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMakePrimary(s.slug);
                  }}
                  className="mt-2 text-[11px] font-medium text-violet-700 hover:underline"
                >
                  Make primary
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
